import type { Task } from '../types';
import type { UserTeam } from '../api/userTeams';

/**
 * Viewer filter values:
 *   'all'            → no filter (everyone on the team)
 *   'self'           → the signed-in user's own assigned tasks
 *   'team:<teamId>'  → tasks whose assignee is a member of user-team <teamId>
 *   '<userSub>'      → tasks assigned to that specific user
 *
 * 'self' is a sentinel (not a real userId) so the persisted state stays
 * stable across sign-ins — resolved to the current user's sub at filter time.
 *
 * `team:<id>` resolves against the caller's **user-teams** (named filter
 * groupings from `/user-teams`), NOT against access-group partitions. An
 * unresolvable id falls through to the empty-set branch (returns the input
 * unchanged) so a stale persisted value doesn't render an empty list.
 */
export type ViewerValue = string;

export const VIEWER_ALL: ViewerValue = 'all';
export const VIEWER_SELF: ViewerValue = 'self';
export const VIEWER_TEAM_PREFIX = 'team:';

export function isTeamViewer(v: ViewerValue): string | null {
  return v.startsWith(VIEWER_TEAM_PREFIX) ? v.slice(VIEWER_TEAM_PREFIX.length) : null;
}

export interface ViewerFilterContext {
  mySub: string | null;
  userTeams: UserTeam[];
}

function membersOfUserTeam(userTeams: UserTeam[], teamId: string): string[] {
  return userTeams.find((t) => t.teamId === teamId)?.members ?? [];
}

/**
 * Narrows a task list by the current viewer selection. Consumers reuse this
 * so filter semantics stay identical across Inbox/Scope/Trash/Completed.
 */
export function filterByViewer(
  tasks: Task[],
  viewer: ViewerValue,
  ctx: ViewerFilterContext
): Task[] {
  if (viewer === VIEWER_ALL) return tasks;
  if (viewer === VIEWER_SELF) {
    if (!ctx.mySub) return tasks;
    return tasks.filter((t) => t.assigneeId === ctx.mySub);
  }
  const teamId = isTeamViewer(viewer);
  if (teamId) {
    const subs = new Set(membersOfUserTeam(ctx.userTeams, teamId));
    if (subs.size === 0) return tasks;
    return tasks.filter((t) => t.assigneeId && subs.has(t.assigneeId));
  }
  return tasks.filter((t) => t.assigneeId === viewer);
}
